import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, Clock, Sparkles } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function Account() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [billing, setBilling] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const [success, setSuccess] = useState(false);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [subBusy, setSubBusy] = useState(false);
  const pollTimer = useRef(null);

  // Initial load
  useEffect(() => {
    api
      .get("/billing/me")
      .then(({ data }) => setBilling(data))
      .catch(() => setBilling(null));
  }, []);

  // Stripe checkout return — poll status
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;
    const POLL_MS = 2000;

    const tick = async () => {
      attempts += 1;
      setPollAttempt(attempts);
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          setBilling(data.access);
          setSuccess(true);
          setPolling(false);
          await checkAuth();
          toast.success("Welcome to Colleague+. Your colleagues are ready.");
          return;
        }
        if (data?.status === "expired") {
          setPolling(false);
          toast.error("Checkout session expired. Try again from Pricing.");
          return;
        }
      } catch (err) {
        // ignore transient errors; keep polling unless we hit max
        if (attempts >= MAX_ATTEMPTS) {
          setPolling(false);
          toast.error(
            "Couldn't confirm payment status. If you paid, please refresh in a minute."
          );
          return;
        }
      }
      if (attempts < MAX_ATTEMPTS) {
        pollTimer.current = setTimeout(tick, POLL_MS);
      } else {
        setPolling(false);
      }
    };
    tick();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const renew = async (passKey) => {
    try {
      const { data } = await api.post("/billing/checkout", {
        pass_key: passKey,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelSub = async () => {
    if (
      !window.confirm(
        "Cancel your Colleague+ subscription? You'll keep access until the end of your current period, then move to the free tier."
      )
    )
      return;
    setSubBusy(true);
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success(data?.message || "Cancellation scheduled.");
      const { data: fresh } = await api.get("/billing/me");
      setBilling(fresh);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubBusy(false);
    }
  };

  const resumeSub = async () => {
    setSubBusy(true);
    try {
      await api.post("/billing/resume");
      toast.success("Welcome back — your subscription will keep renewing.");
      const { data: fresh } = await api.get("/billing/me");
      setBilling(fresh);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubBusy(false);
    }
  };

  if (!user) return null;

  const statusLabel =
    billing?.status === "active"
      ? "Colleague+"
      : billing?.status === "trial"
      ? "Free trial"
      : "Expired";

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main
        data-testid="account-page"
        className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2926] tracking-tight">
          Account
        </h1>

        {/* Stripe-return success banner */}
        {success && (
          <div
            data-testid="account-payment-success"
            className="mt-6 bg-[#FBF6EE] border border-[#D4A574] rounded-2xl p-5 flex items-start gap-3"
          >
            <CheckCircle2
              size={22}
              strokeWidth={1.5}
              className="text-[#D4A574] mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="font-serif text-xl text-[#2D2926]">
                You&apos;re in. Welcome to Colleague+.
              </p>
              <p className="mt-1 text-sm text-[#6B655D]">
                Your access is active until {formatDate(billing?.effective_until)}.
                Everyone&apos;s ready when you are.
              </p>
              <Link
                to="/dashboard"
                className="inline-block mt-3 text-xs uppercase tracking-wider text-[#D4A574] hover:text-[#C99560]"
              >
                Back to Dashboard →
              </Link>
            </div>
          </div>
        )}

        {polling && !success && (
          <div
            data-testid="account-payment-polling"
            className="mt-6 bg-white border border-[#E6E2D8] rounded-2xl p-5 flex items-center gap-3"
          >
            <Clock
              size={18}
              strokeWidth={1.5}
              className="text-[#6B655D] animate-pulse"
            />
            <p className="text-sm text-[#6B655D]">
              Confirming your payment with Stripe… ({pollAttempt}/10)
            </p>
          </div>
        )}

        {/* Plan summary */}
        <div className="mt-8 bg-white border border-[#E6E2D8] rounded-3xl p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A]">
                Current plan
              </p>
              <h2 className="mt-1 font-serif text-2xl text-[#2D2926]">
                {statusLabel}
              </h2>
            </div>
            <Sparkles size={18} strokeWidth={1.5} className="text-[#D4A574] mt-1" />
          </div>

          {billing && (
            <dl className="mt-6 space-y-3 text-sm">
              {billing.in_trial && (
                <div className="flex justify-between gap-3">
                  <dt className="text-[#6B655D]">Trial ends</dt>
                  <dd className="text-[#2D2926]">
                    {formatDate(billing.trial_ends_at)} · {billing.days_remaining}{" "}
                    day{billing.days_remaining === 1 ? "" : "s"} left
                  </dd>
                </div>
              )}
              {billing.paid_active && (
                <div className="flex justify-between gap-3">
                  <dt className="text-[#6B655D]">
                    {billing.cancel_at_period_end
                      ? "Access until"
                      : "Renews on"}
                  </dt>
                  <dd className="text-[#2D2926]">
                    {formatDate(billing.access_expires_at)}
                  </dd>
                </div>
              )}
              {billing.status === "expired" && (
                <p className="text-[#6B655D]">
                  Your access ended. Your conversations are all safe — subscribe
                  to start sending again.
                </p>
              )}
              {billing.cancel_at_period_end && (
                <p
                  data-testid="cancel-scheduled-note"
                  className="text-sm text-[#8C6A2F] bg-[#FBF3E4] border border-[#E6D9C4] rounded-xl px-3 py-2"
                >
                  Cancellation scheduled. You&apos;ll keep Colleague+ until{" "}
                  {formatDate(billing.access_expires_at)}, then move to the free
                  tier. Change your mind? You can resume any time.
                </p>
              )}
            </dl>
          )}

          {billing?.has_subscription && billing?.paid_active ? (
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {billing.cancel_at_period_end ? (
                <button
                  type="button"
                  data-testid="account-resume-btn"
                  onClick={resumeSub}
                  disabled={subBusy}
                  className="bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {subBusy ? "Resuming…" : "Resume subscription"}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="account-cancel-btn"
                  onClick={cancelSub}
                  disabled={subBusy}
                  className="border border-[#E6E2D8] text-[#6B655D] hover:border-[#D98074] hover:text-[#D98074] rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {subBusy ? "Canceling…" : "Cancel subscription"}
                </button>
              )}
            </div>
          ) : (
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="account-renew-monthly"
                onClick={() => renew("monthly")}
                className="bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
              >
                Subscribe monthly — $9.99
              </button>
              <button
                type="button"
                data-testid="account-renew-yearly"
                onClick={() => renew("yearly")}
                className="border border-[#D4A574] text-[#2D2926] hover:bg-[#FBF6EE] rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
              >
                Subscribe yearly — $99
              </button>
            </div>
          )}
          <p className="mt-3 text-xs text-[#9A938A]">
            Tax included where applicable. Recurring subscription — cancel any
            time.
          </p>
        </div>

        <ChangePasswordSection />

        <div className="mt-6">
          <Link
            to="/profile"
            className="text-sm text-[#6B655D] hover:text-[#2D2926] transition-colors"
          >
            ← Manage profile
          </Link>
        </div>
      </main>
    </div>
  );
}

function ChangePasswordSection() {
  const { user } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // Google-auth users don't have a local password; hide the section entirely.
  if (user?.auth_provider && user.auth_provider !== "password") return null;

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPw,
        new_password: newPw,
      });
      toast.success("Password updated.");
      setOldPw("");
      setNewPw("");
      setConfirmPw("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="change-password-section"
      className="surface-card p-6 sm:p-8 mt-6"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#2D2926]">Password</h2>
          <p className="mt-1 text-sm text-[#6B655D]">
            Change the password you use to sign in.
          </p>
        </div>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="open-change-password-btn"
            className="text-sm border border-[#E6E2D8] text-[#2D2926] hover:border-[#D4A574] rounded-full px-4 py-2 transition-colors"
          >
            Change password
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-6 space-y-4 max-w-md">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#9A938A] mb-1">
              Current password
            </label>
            <input
              type="password"
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              required
              data-testid="change-pw-current"
              autoComplete="current-password"
              className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#9A938A] mb-1">
              New password
            </label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
              data-testid="change-pw-new"
              autoComplete="new-password"
              className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
            />
            <p className="mt-1 text-xs text-[#9A938A]">
              Minimum 8 characters.
            </p>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#9A938A] mb-1">
              Confirm new password
            </label>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              data-testid="change-pw-confirm"
              autoComplete="new-password"
              className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="change-pw-submit"
              className="bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setOldPw("");
                setNewPw("");
                setConfirmPw("");
              }}
              className="border border-[#E6E2D8] text-[#6B655D] hover:border-[#2D2926] rounded-full px-5 py-2.5 text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
